import { useSyncExternalStore } from 'react';
import { connectorsStore } from '../collections/diagramCollections';

// Subscribers for connector changes
const connectorSubscribers = new Set<() => void>();

// Notify all subscribers when connectors change
export function notifyConnectorChange() {
  connectorSubscribers.forEach((callback) => callback());
}

/**
 * Hook for all connectors in the diagram
 * Reactive - re-renders when connectors change
 */
export function useDiagramConnectors() {
  return useSyncExternalStore(
    (callback) => {
      connectorSubscribers.add(callback);
      return () => connectorSubscribers.delete(callback);
    },
    () => Array.from(connectorsStore.values())
  );
}

/**
 * Hook for a single connector by ID
 */
export function useDiagramConnector(connectorId: string | null) {
  return useSyncExternalStore(
    (callback) => {
      connectorSubscribers.add(callback);
      return () => connectorSubscribers.delete(callback);
    },
    () => {
      if (!connectorId) return null;
      return connectorsStore.get(connectorId) || null;
    }
  );
}

/**
 * Hook for connectors attached to a specific shape
 * Useful for updating connected lines when a shape moves
 */
export function useConnectorsByShape(shapeId: string | null) {
  return useSyncExternalStore(
    (callback) => {
      connectorSubscribers.add(callback);
      return () => connectorSubscribers.delete(callback);
    },
    () => {
      if (!shapeId) return [];
      
      return Array.from(connectorsStore.values())
        .filter((c) => c.sourceShapeId === shapeId || c.targetShapeId === shapeId);
    }
  );
}

/**
 * Hook for connectors between two specific shapes
 */
export function useConnectorsBetweenShapes(sourceId: string | null, targetId: string | null) {
  return useSyncExternalStore(
    (callback) => {
      connectorSubscribers.add(callback);
      return () => connectorSubscribers.delete(callback);
    },
    () => {
      if (!sourceId || !targetId) return [];
      
      return Array.from(connectorsStore.values())
        .filter((c) => 
          (c.sourceShapeId === sourceId && c.targetShapeId === targetId) ||
          (c.sourceShapeId === targetId && c.targetShapeId === sourceId)
        );
    }
  );
}

